import { Form, Input, Select } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FormModal } from "@/shared/components/FormModal";
import { useDebouncedValue } from "@/shared/hooks/useDebouncedValue";
import { useDepartmentOptions, useManagerOptions, usePositionOptions } from "../hooks/useEmployees";
import { type Employee, type EmployeeFormValues, HR_STATUSES, hrStatusLabel } from "../types";
import { EmployeePhotoField } from "./EmployeePhotoField";

/** Bir keystroke'da bitta so'rov ketmasin — foydalanuvchi yozishni to'xtatgach so'raladi. */
const MANAGER_SEARCH_DEBOUNCE_MS = 300;

interface EmployeeFormModalProps {
	open: boolean;
	editing: Employee | null;
	isSubmitting: boolean;
	onCancel: () => void;
	onSubmit: (values: EmployeeFormValues) => void;
	/**
	 * Bo'lim profili ichidan qo'shish: bo'lim oldindan tanlanadi va
	 * o'zgartirib BO'LMAYDI (Select `disabled`), lavozimlar shu bo'lim
	 * darajasining rosteriga filtrlanadi.
	 */
	lockedDepartmentId?: string;
}

/**
 * Lavozim maydoni — tanlangan bo'lim darajasining rosteri
 * (`GET /positions?departmentId=`) bilan filtrlanadi; bo'lim tanlanmagan
 * bo'lsa barcha lavozimlar.
 *
 * ⚠️ Bo'lim FOYDALANUVCHI tomonidan almashtirilib (`initialDepartmentId`
 * dan farqli), tanlangan lavozim yangi rosterda bo'lmasa — lavozim
 * TOZALANADI: aks holda u jimgina qolib, saqlashda backend 400
 * (`positionNotAllowedForGrade`) berardi. Forma ochilgandagi bo'lim uchun
 * tozalanmaydi — tahrirlashda mavjud qiymat o'z-o'zidan yo'qolmasin.
 */
function PositionField({ initialDepartmentId }: { initialDepartmentId?: string }) {
	const { t } = useTranslation("employees");
	const form = Form.useFormInstance<EmployeeFormValues>();
	const departmentId = Form.useWatch("departmentId", form);
	const { data: positions = [], isSuccess } = usePositionOptions(departmentId);

	useEffect(() => {
		if (!(departmentId && isSuccess) || departmentId === initialDepartmentId) {
			return;
		}
		const current = form.getFieldValue("positionId");
		if (current && !positions.some((p) => p.id === current)) {
			form.setFieldValue("positionId", undefined);
		}
	}, [departmentId, initialDepartmentId, isSuccess, positions, form]);

	const rosterBosh = Boolean(departmentId) && isSuccess && positions.length === 0;

	return (
		<Form.Item
			name="positionId"
			label={t("form.positionLabel")}
			extra={rosterBosh ? t("form.positionRosterEmpty") : undefined}
		>
			<Select
				allowClear
				placeholder={t("form.positionPlaceholder")}
				options={positions.map((p) => ({ value: p.id, label: p.name }))}
				showSearch={{ optionFilterProp: "label" }}
			/>
		</Form.Item>
	);
}

export function EmployeeFormModal({
	open,
	editing,
	isSubmitting,
	onCancel,
	onSubmit,
	lockedDepartmentId,
}: EmployeeFormModalProps) {
	const { t } = useTranslation("employees");
	const isEditing = editing !== null;
	const hrStatusOptions = HR_STATUSES.map((status) => ({
		value: status,
		label: hrStatusLabel(t, status),
	}));

	const { data: departments = [] } = useDepartmentOptions();

	// Rahbar tanlash — 100 tadan ko'p xodim bo'lishi mumkin, shuning uchun
	// mijozda bitta sahifani filtrlash o'rniga server tomonida qidiriladi
	// (`q` parametri). Har harfda so'rov ketmasligi uchun debounce qilinadi.
	const [managerSearch, setManagerSearch] = useState("");
	const debouncedManagerSearch = useDebouncedValue(managerSearch, MANAGER_SEARCH_DEBOUNCE_MS);
	const { data: managerCandidates = [], isFetching: isManagerSearchLoading } =
		useManagerOptions(debouncedManagerSearch);

	useEffect(() => {
		if (open) {
			setManagerSearch("");
		}
	}, [open]);

	// ⚠️ `useMemo` MAJBURIY — `FormModal` `initialValues` havolasi o'zgarganda
	// `resetFields()` chaqiradi. Har renderda yangi obyekt bo'lsa, rahbar
	// qidiruvi kabi ichki holat o'zgarishi kiritilgan qiymatlarni o'chirardi.
	const initialValues = useMemo(() => {
		if (editing) {
			return {
				fullName: editing.fullName,
				idCode: editing.idCode ?? undefined,
				positionId: editing.positionId ?? undefined,
				departmentId: editing.departmentId ?? undefined,
				managerId: editing.managerId ?? undefined,
				hrStatus: editing.hrStatus,
			};
		}
		return lockedDepartmentId ? { departmentId: lockedDepartmentId } : undefined;
	}, [editing, lockedDepartmentId]);

	const departmentOptions = departments.map((d) => ({ value: d.id, label: d.name }));
	// Xodim o'zini o'ziga rahbar qilib tanlay olmaydi.
	const managerOptions = managerCandidates
		.filter((candidate) => candidate.id !== editing?.id)
		.map((candidate) => ({ value: candidate.id, label: candidate.fullName }));

	return (
		<FormModal<EmployeeFormValues>
			open={open}
			title={isEditing ? t("form.editTitle", { ism: editing.fullName }) : t("form.newTitle")}
			initialValues={initialValues}
			isSubmitting={isSubmitting}
			onCancel={onCancel}
			onSubmit={onSubmit}
		>
			<Form.Item
				name="fullName"
				label={t("form.fullNameLabel")}
				rules={[{ required: true, message: t("form.fullNameRequired") }]}
			>
				<Input placeholder={t("form.fullNamePlaceholder")} />
			</Form.Item>

			{!isEditing && (
				<Form.Item
					name="phone"
					label={t("form.phoneLabel")}
					rules={[
						{ required: true, message: t("form.phoneRequired") },
						{ pattern: /^\+?[1-9]\d{1,14}$/, message: t("form.phoneFormat") },
					]}
				>
					<Input placeholder="+998901234567" autoComplete="tel" />
				</Form.Item>
			)}

			<Form.Item name="idCode" label={t("form.idCodeLabel")}>
				<Input placeholder={t("form.idCodePlaceholder")} />
			</Form.Item>

			<Form.Item name="departmentId" label={t("form.departmentLabel")}>
				<Select
					allowClear={!lockedDepartmentId}
					disabled={Boolean(lockedDepartmentId)}
					placeholder={t("form.departmentPlaceholder")}
					options={departmentOptions}
					showSearch={{ optionFilterProp: "label" }}
				/>
			</Form.Item>

			<PositionField initialDepartmentId={initialValues?.departmentId} />

			<Form.Item name="managerId" label={t("form.managerLabel")}>
				<Select
					allowClear
					placeholder={t("form.managerPlaceholder")}
					options={managerOptions}
					showSearch={{
						// Qidiruv natijalari server tomonida keladi — mijozda qayta
						// filtrlash kerak emas (aks holda faqat oxirgi so'ralgan
						// ro'yxat ichida qidirilib, boshqa nomzodlar yashiringan bo'lardi).
						filterOption: false,
						onSearch: setManagerSearch,
					}}
					loading={isManagerSearchLoading}
					notFoundContent={
						isManagerSearchLoading ? t("form.managerSearching") : t("form.managerNotFound")
					}
				/>
			</Form.Item>

			{isEditing && (
				<Form.Item name="hrStatus" label={t("form.hrStatusLabel")}>
					<Select options={hrStatusOptions} />
				</Form.Item>
			)}

			{/*
			 * Rasm maydoni FAQAT tahrirlashda (spec §10): Face AI guruhi
			 * `employeeId` ustiga quriladi, ya'ni xodim yozilmagunicha rasm
			 * biriktiriladigan guruh yo'q. `name` prop'i ATAYLAB yo'q — maydon
			 * forma qiymatlariga kirmaydi, u o'z marshrutiga darhol yozadi.
			 */}
			{isEditing && (
				<Form.Item label={t("form.photoLabel")}>
					<EmployeePhotoField employee={editing} />
				</Form.Item>
			)}
		</FormModal>
	);
}
